// Register.js
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  resolveRegisterVerifyEntry,
  searchParamsToVerifySearch,
} from "../../utils/loginVerifyUtils";
import { resolveDeepLinkVerificationCode } from "../../utils/deepLinkVerificationCode";
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import ProgressBar from "./ProgressBar";
import VerifyCode from "./VerifyCode";
import { userRegister } from "./registerCall";
import { subscribeConsent } from "../../utils/consent";
import { trackRegistration } from "../../utils/klaviyo";
import "./Register.css";
import { useTranslation } from "react-i18next";

function Register() {
  const { t } = useTranslation("register");

  // Steps: 1 = Basic Info, 2 = Legal Info, 3 = Verify Code
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    firstName: "",
    familyName: "",
    email: "",
    phoneNumber: "",
    password: "",
    passwordConfirm: "",
    company: "",
    commercialRecord: "",
    taxNumber: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [validUserError, setValidUserError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const search = searchParamsToVerifySearch(searchParams);
  const registerVerifyEntry = resolveRegisterVerifyEntry(search);
  const registerVerifyEmail =
    registerVerifyEntry?.email ?? search.email ?? "";
  const verificationCodeFromUrl = registerVerifyEntry
    ? resolveDeepLinkVerificationCode(
        "register",
        registerVerifyEmail,
        search.code,
      )
    : "";

  const isVerifyDeepLink = Boolean(registerVerifyEntry);

  useEffect(() => {
    if (isVerifyDeepLink || step === 3) return;
    const send = () => trackRegistration("started", "Vendor");
    send();
    return subscribeConsent(send);
  }, [isVerifyDeepLink, step]);

  useEffect(() => {
    if (!registerVerifyEntry) {
      return;
    }
    setStep(3);
    setFormData((prev) => ({
      ...prev,
      email: registerVerifyEntry.email,
    }));
  }, [registerVerifyEntry?.email]);

  useEffect(() => {
    if (!registerVerifyEntry?.email || !search.code) {
      return;
    }
    const params = new URLSearchParams({
      isVerify: "true",
      email: registerVerifyEntry.email,
    });
    navigate(`/register?${params.toString()}`, { replace: true });
  }, [navigate, registerVerifyEntry?.email, search.code]);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword((prev) => !prev);
  };

  // Custom validation for Step One (Basic Information)
  const validateStepOne = (data = formData) => {
    const errors = {};
    // First Name
    if (!data.firstName.trim()) {
      errors.firstName = t("validation.firstName.required");
    } else if (data.firstName.trim().length < 3) {
      errors.firstName = t("validation.firstName.min");
    }
    // Family Name
    if (!data.familyName.trim()) {
      errors.familyName = t("validation.familyName.required");
    } else if (data.familyName.trim().length < 3) {
      errors.familyName = t("validation.familyName.min");
    }
    // Company
    if (!data.company.trim()) {
      errors.company = t("validation.company.required");
    } else if (data.company.trim().length < 3) {
      errors.company = t("validation.company.min");
    }
    // Commercial Record
    if (!data.commercialRecord.trim()) {
      errors.commercialRecord = t("validation.commercialRecord.required");
    } else if (data.commercialRecord.trim().length !== 10) {
      errors.commercialRecord = t("validation.commercialRecord.length");
    }
    // Tax Number
    if (!data.taxNumber.trim()) {
      errors.taxNumber = t("validation.taxNumber.required");
    } else if (data.taxNumber.trim().length !== 15) {
      errors.taxNumber = t("validation.taxNumber.length");
    }
    return errors;
  };

  // Custom validation for Step Two (Contact & Security Information)
  const validateStepTwo = (data = formData) => {
    const errors = {};
    // Email
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!data.email.trim()) {
      errors.email = t("validation.email.required");
    } else if (!emailRegex.test(data.email)) {
      errors.email = t("validation.email.invalid");
    }
    // Phone Number (Saudi or Egypt format)
    const phoneRegex = /^((\+966)|(00966)|0)?5[0-9]{8}$|^((\+20)|(0020)|0)?1[0-9]{9}$/;
    if (!data.phoneNumber.trim()) {
      errors.phoneNumber = t("validation.phone.required");
    } else if (!phoneRegex.test(data.phoneNumber)) {
      errors.phoneNumber = t("validation.phone.invalid");
    }
    // Password
    if (!data.password) {
      errors.password = t("validation.password.required");
    } else if (data.password.length < 8) {
      errors.password = t("validation.password.length");
    } else {
      if (!/[A-Z]/.test(data.password)) {
        errors.password = t("validation.password.uppercase");
      } else if (!/[a-z]/.test(data.password)) {
        errors.password = t("validation.password.lowercase");
      } else if (!/\d/.test(data.password)) {
        errors.password = t("validation.password.number");
      } else if (!/[^A-Za-z0-9]/.test(data.password)) {
        errors.password = t("validation.password.special");
      }
    }
    // Confirm Password
    if (!data.passwordConfirm) {
      errors.passwordConfirm = t("validation.passwordConfirm.required");
    } else if (data.passwordConfirm !== data.password) {
      errors.passwordConfirm = t("validation.passwordConfirm.match");
    }
    return errors;
  };

  // Generic change handler for all inputs with real-time validation and trim email and phone number input
  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "email" || name === "phoneNumber") {
      newValue = value.trim();
    }

    const newFormData = { ...formData, [name]: newValue };
    setFormData(newFormData);

    if (step === 1) {
      setFormErrors(validateStepOne(newFormData));
    } else if (step === 2) {
      setFormErrors(validateStepTwo(newFormData));
    }
  };

  // Generic blur handler to mark fields as touched
  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    let errors = {};
    if (step === 1) {
      errors = validateStepOne();
      setTouched({
        firstName: true,
        familyName: true,
        company: true,
        commercialRecord: true,
        taxNumber: true,
      });
    } else if (step === 2) {
      errors = validateStepTwo();
      setTouched({
        email: true,
        phoneNumber: true,
        password: true,
        passwordConfirm: true,
      });
    }
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      try {
        setLoading(true);
        setValidUserError("");
        const response = await userRegister(formData);
        if (response.status === 200 || response.status === 201) {
          setStep(3);
        }
      } catch (error) {
        const errorResponse = error.response;
        if (errorResponse?.errors && Array.isArray(errorResponse.errors)) {
          const newErrors = {};
          errorResponse.errors.forEach((errObj) => {
            const key = Object.keys(errObj)[0];
            newErrors[key] = errObj[key];
          });
          setFormErrors((prev) => ({ ...prev, ...newErrors }));
          const newTouched = {};
          Object.keys(newErrors).forEach((key) => {
            newTouched[key] = true;
          });
          setTouched((prev) => ({ ...prev, ...newTouched }));

          const stepOneFields = [
            "firstName",
            "familyName",
            "company",
            "commercialRecord",
            "taxNumber",
          ];
          const hasStepOneError = Object.keys(newErrors).some((k) =>
            stepOneFields.includes(k)
          );
          setStep(hasStepOneError ? 1 : 2);
        } else {
          const errorMessage =
            errorResponse?.error ||
            errorResponse?.message ||
            t("messages.unexpected");
          const lowerCaseError = errorMessage.toLowerCase();

          if (lowerCaseError.includes("email") && lowerCaseError.includes("phone")) {
            setFormErrors((prev) => ({
              ...prev,
              email: errorMessage,
              phoneNumber: errorMessage,
            }));
            setTouched((prev) => ({ ...prev, email: true, phoneNumber: true }));
            setStep(2);
          } else if (lowerCaseError.includes("email")) {
            setFormErrors((prev) => ({ ...prev, email: errorMessage }));
            setTouched((prev) => ({ ...prev, email: true }));
            setStep(2);
          } else if (lowerCaseError.includes("phone")) {
            setFormErrors((prev) => ({ ...prev, phoneNumber: errorMessage }));
            setTouched((prev) => ({ ...prev, phoneNumber: true }));
            setStep(2);
          } else {
            setValidUserError(errorMessage);
          }
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  // Step 3: Verification Code form
  if (step === 3) {
    return (
      <div className="register-container-unique">
        <div className="register-left-unique">
          <div className="register-logo">
            <img src="/logo.svg" alt="logo" />
          </div>
          <div className="overlay-text-unique">
            <div className="register-text">
              <h1 className="register-title">{t("hero.headline")}</h1>
              <p className="register-subtitle">{t("hero.subtitle")}</p>
            </div>
          </div>
        </div>
        <div className="register-right-unique">
          <div className="form-container-unique">
            <VerifyCode
              key={`${registerVerifyEmail || formData.email}-${verificationCodeFromUrl}`}
              email={registerVerifyEmail || formData.email}
              initialCode={verificationCodeFromUrl}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-container-unique">
      <div className="register-left-unique">
        <div className="register-logo">
          <img src="/logo.svg" alt="logo" />
        </div>
        <div className="overlay-text-unique">
          <div className="register-text">
            <h1 className="register-title">{t("hero.headline")}</h1>
            <p className="register-subtitle">{t("hero.subtitle")}</p>
          </div>
        </div>
      </div>
      <div className="register-right-unique">
        <div className="form-container-unique">
          <h1 className="login-title-unique">{t("titles.complete")}</h1>
          <p className="login-sub-title-unique">{t("titles.signUpSubtitle")}</p>

          {/* Progress bar rendered for steps 1 & 2 */}
          <ProgressBar step={step} />

          <form onSubmit={handleFinalSubmit}>
            {step === 1 ? (
              <StepOne
                formData={formData}
                formErrors={formErrors}
                touched={touched}
                validUserError={validUserError}
                handleChange={handleChange}
                handleBlur={handleBlur}
              />
            ) : (
              <StepTwo
                formData={formData}
                formErrors={formErrors}
                touched={touched}
                validUserError={validUserError}
                handleChange={handleChange}
                handleBlur={handleBlur}
                setStep={setStep}
                showPassword={showPassword}
                showConfirmPassword={showConfirmPassword}
                togglePasswordVisibility={togglePasswordVisibility}
                toggleConfirmPasswordVisibility={
                  toggleConfirmPasswordVisibility
                }
              />
            )}

            <button type="submit" disabled={loading} className="btn-register-unique">
              {step === 1
                ? t("buttons.next")
                : loading
                ? t("buttons.registering")
                : t("buttons.signUp")}
            </button>

            <div className="Navigation">
              <span className="forgot-password-span">{t("messages.haveAccount")}</span>
              <button
                type="button"
                onClick={handleLoginClick}
                className="forgot-password-unique text-[#FFC107]"
              >
                {t("buttons.login")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Register;
